var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { Box, Paper, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { lazy, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createShopAction, deleteShopAction, updateShopAction } from '../../../api/action/shop.action';
import { QUERY_KEY } from '../../../api/types/query.type';
import { ALERT_DURATION } from '../../../constants/common.constants';
import { useSettings } from '../../../context';
import { createOrUpdateShopFormSchema } from '../../../schemas/shop.schemas';
import { FORM_INPUTS } from './form.costants';
import { theme } from './theme';
var AlertComponent = lazy(function () { return import('../../UIcomponents/AlertComponent/AlertComponent'); });
var ButtonComponent = lazy(function () { return import('../../UIcomponents/ButtonComponent/ButtonComponent'); });
var Heading = lazy(function () { return import('../../UIcomponents/Heading'); });
var EXIST_SHOP_STATUS_CODE = '409';
var ShopForm = function (_a) {
    var shopId = _a.shopId, name = _a.name, domain = _a.domain, onDeleteSuccess = _a.onDeleteSuccess;
    var _b = useState(false), needToShowDeleteShopAlert = _b[0], setNeedToShowDeleteShopAlert = _b[1];
    var _c = useState(false), needToShowExistShopAlert = _c[0], setNeedToShowExistShopAlert = _c[1];
    var _d = useState(false), needToShowNewShopAlert = _d[0], setNeedToShowNewShopAlert = _d[1];
    var _e = useState(false), needToShowErrorAlert = _e[0], setNeedToShowErrorAlert = _e[1];
    var t = useTranslation('settings').t;
    var shopSettings = useSettings().shopSettings;
    var _f = useForm({
        resolver: zodResolver(createOrUpdateShopFormSchema()),
        defaultValues: {
            name: name || '',
            domain: domain || '',
        },
        mode: 'all',
    }), control = _f.control, handleSubmit = _f.handleSubmit, getValues = _f.getValues, reset = _f.reset;
    var queryClient = useQueryClient();
    var _g = useMutation({
        mutationFn: createShopAction,
        onSuccess: function () {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_SHOPS],
            });
            setNeedToShowNewShopAlert(true);
            setTimeout(function () {
                setNeedToShowNewShopAlert(false);
            }, ALERT_DURATION);
        },
        onError: function (error) {
            var _a, _b;
            var status = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.toString();
            var isExistShop = status === EXIST_SHOP_STATUS_CODE;
            if (isExistShop) {
                setNeedToShowExistShopAlert(true);
                setTimeout(function () {
                    setNeedToShowExistShopAlert(false);
                }, ALERT_DURATION);
            }
            else {
                setNeedToShowErrorAlert(true);
                setTimeout(function () {
                    setNeedToShowErrorAlert(false);
                }, ALERT_DURATION);
            }
        },
    }), createShop = _g.mutate, isCreatingShop = _g.isPending;
    var _h = useMutation({
        mutationFn: updateShopAction,
        onError: function () {
            setNeedToShowErrorAlert(true);
            setTimeout(function () {
                setNeedToShowErrorAlert(false);
            }, ALERT_DURATION);
        },
    }), updateShop = _h.mutate, isUpdatingShop = _h.isPending;
    var _j = useMutation({
        mutationFn: deleteShopAction,
        onSuccess: function () {
            onDeleteSuccess();
            setNeedToShowDeleteShopAlert(!needToShowDeleteShopAlert);
            // Reset form data after successful deletion
            reset({
                name: '',
                domain: '',
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_SHOPS],
            });
        },
        onError: function () {
            setNeedToShowErrorAlert(true);
            setTimeout(function () {
                setNeedToShowErrorAlert(false);
            }, ALERT_DURATION);
        },
    }), deleteShop = _j.mutate, isDeletingShop = _j.isPending;
    var handleDeleteShopAlert = function () {
        setNeedToShowDeleteShopAlert(!needToShowDeleteShopAlert);
    };
    var handleDeleteShop = function () {
        deleteShop(shopId);
    };
    var onUpdateShop = useCallback(function () {
        var data = getValues();
        updateShop({ data: data, id: shopId });
    }, [getValues, updateShop, shopId, reset]);
    var onSubmit = useCallback(function (data) {
        createShop(__assign({}, data));
    }, [createShop]);
    var renderActionSection = function () {
        return (_jsxs(Box, { children: [_jsx(Box, { sx: {
                        visibility: needToShowNewShopAlert ? 'visible' : 'hidden',
                        height: needToShowNewShopAlert ? 'auto' : '0px',
                        overflow: 'hidden',
                    }, children: _jsx(AlertComponent, { type: "success", message: t('shop.newShop') }) }), _jsx(Box, { sx: {
                        visibility: needToShowExistShopAlert ? 'visible' : 'hidden',
                        height: needToShowExistShopAlert ? 'auto' : '0px',
                        overflow: 'hidden',
                    }, children: _jsx(AlertComponent, { type: "error", message: t('shop.existShop') }) }), _jsx(Box, { sx: {
                        visibility: needToShowDeleteShopAlert ? 'visible' : 'hidden',
                        height: needToShowDeleteShopAlert ? 'auto' : '0px',
                        overflow: 'hidden',
                    }, children: _jsx(AlertComponent, { type: "warning", style: { fontSize: '18px' }, message: t('shop.confirmDeleteMessage'), component: _jsxs(Box, { style: { display: 'flex', gap: 2 }, children: [_jsx(ButtonComponent, { title: t('common.cancel'), stylesType: "cancel", handleClick: handleDeleteShopAlert }), _jsx(ButtonComponent, { title: t('common.confirm'), handleClick: handleDeleteShop, isDisabled: isDeletingShop })] }) }) }), _jsx(Box, { sx: {
                        visibility: needToShowErrorAlert ? 'visible' : 'hidden',
                        height: needToShowErrorAlert ? 'auto' : '0px',
                        overflow: 'hidden',
                    }, children: _jsx(AlertComponent, { type: "error", message: t('shop.error.somethingWentWrong') }) }), _jsx(Box, { sx: {
                        visibility: !needToShowNewShopAlert && !needToShowExistShopAlert && !needToShowDeleteShopAlert && !needToShowErrorAlert
                            ? 'visible'
                            : 'hidden',
                        height: !needToShowNewShopAlert && !needToShowExistShopAlert && !needToShowDeleteShopAlert && !needToShowErrorAlert
                            ? 'auto'
                            : '0px',
                        overflow: 'hidden',
                    }, children: _jsxs(Stack, { direction: "row", spacing: 2, justifyContent: "center", children: [!!domain && _jsx(ButtonComponent, { title: t('shop.delete'), stylesType: "cancel", handleClick: handleDeleteShopAlert }), !!domain ? (_jsx(ButtonComponent, { title: t('shop.refresh'), isDisabled: isUpdatingShop, handleClick: onUpdateShop })) : (_jsx(ButtonComponent, { title: t('shop.submit'), type: "submit", isDisabled: isCreatingShop }))] }) })] }));
    };
    return (_jsxs(Paper, { sx: theme.container, children: [_jsxs(Box, { sx: theme.heading, children: [_jsx(StorefrontIcon, { sx: shopSettings.isShopAdded ? theme.iconCompleted : undefined }), _jsx(Heading, { title: "shop.title" })] }), _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), sx: theme.form, children: [_jsx(Box, { sx: theme.inputsWrapper, children: FORM_INPUTS.map(function (_a) {
                            var shopName = _a.name, label = _a.label, placeholder = _a.placeholder;
                            return (_jsx(Controller, { name: shopName, control: control, render: function (_a) {
                                    var field = _a.field, error = _a.fieldState.error;
                                    return (_jsx(TextField, __assign({ fullWidth: true }, field, { label: t(label), placeholder: t(placeholder), variant: "outlined", error: !!error, helperText: error ? t(error.message) : null })));
                                } }, label));
                        }) }), renderActionSection()] })] }));
};
export default ShopForm;
